import { isPlainObject } from 'es-toolkit/predicate';

export { isPlainObject };

function unwrapEitherError(value: unknown): unknown {
  if (!isPlainObject(value)) {
    return value;
  }

  const tag = value._tag;
  if (tag === 'Left' && 'left' in value) {
    return value.left;
  }

  return value;
}

function collectSearchFragments(error: unknown): string {
  const fragments: string[] = [];
  const visited = new Set<object>();

  const visit = (value: unknown, depth: number) => {
    if (value === null || value === undefined || depth > 4) {
      return;
    }

    if (typeof value === 'string') {
      fragments.push(value);
      return;
    }

    if (typeof value !== 'object') {
      return;
    }

    if (visited.has(value)) {
      return;
    }
    visited.add(value);

    const record = value as {
      name?: unknown;
      message?: unknown;
      cause?: unknown;
      error?: unknown;
      originalError?: unknown;
    };

    if (typeof record.name === 'string') {
      fragments.push(record.name);
    }

    if (typeof record.message === 'string') {
      fragments.push(record.message);
    }

    visit(record.cause, depth + 1);
    visit(record.error, depth + 1);
    visit(record.originalError, depth + 1);
  };

  visit(error, 0);

  return fragments.join(' ').toLowerCase();
}

export interface ExtractedErrorInfo {
  error: Error | string;
  message: string;
  name?: string;
  searchText: string;
}

export function extractErrorInfo(
  error: unknown,
  options: {
    defaultMessage?: string;
  } = {},
): ExtractedErrorInfo {
  const unwrapped = unwrapEitherError(error);

  if (unwrapped instanceof Error) {
    return {
      error: unwrapped,
      message: unwrapped.message,
      name: unwrapped.name,
      searchText: collectSearchFragments(unwrapped),
    };
  }

  if (typeof unwrapped === 'string') {
    return {
      error: unwrapped,
      message: unwrapped,
      searchText: collectSearchFragments(unwrapped),
    };
  }

  if (isPlainObject(unwrapped)) {
    const errorLike = unwrapped as { name?: unknown; message?: unknown };
    const name =
      typeof errorLike.name === 'string' ? errorLike.name : undefined;
    const message =
      typeof errorLike.message === 'string'
        ? errorLike.message
        : errorLike.message != null
          ? String(errorLike.message)
          : undefined;

    if (name || message) {
      const normalizedMessage = message || 'Unknown error';
      const normalizedError = new Error(normalizedMessage);
      if (name) {
        normalizedError.name = name;
      }

      return {
        error: normalizedError,
        message: normalizedError.message,
        name: normalizedError.name,
        searchText: collectSearchFragments(unwrapped),
      };
    }
  }

  const fallbackMessage =
    unwrapped == null
      ? (options.defaultMessage ?? String(unwrapped))
      : String(unwrapped);

  return {
    error: fallbackMessage,
    message: fallbackMessage,
    searchText: collectSearchFragments(unwrapped),
  };
}
